import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useTranslation} from 'react-i18next';
import {useInfiniteQuery, useMutation, UseMutationResult, useQuery} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import {useParams} from 'react-router-dom';
import {QUERY_KEYS} from '../../../../utils/queryKeys';
import {loggedInUserSelector} from '../../../../store/selectors/auth.selectors';
import {prettyDateFormat} from '../../../../utils/dateTimeUtils';
import {IUpdateError} from '../../../../store/actions/types/error.types';
import {deleteConversation, fetchMessages, fetchUnseenMessages, sendMessage} from '../../../../service/message';
import {IConversation, IMessage} from '../../../../incase-domain/message';
import {randomString, removeHtmlTagsAndSpaces} from '../../../util/util';
import Button from '../../../../incase-domain/components/button/Button';
import DropdownMenuCompact, {
    DropdownMenuCompactMenuPosition,
} from '../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import ConfirmModal from '../../../../incase-domain/components/modals/confirmModal/ConfirmModal';
import i18n from '../../../../i18n';
import {MAX_MESSAGE_LENGTH, messageValidator} from '../../../../incase-domain/validators/conversation/message';
import TooltipWrapper from '../../../components/common/TooltipWrapper';
import TextAreaWithCharacterCounter from '../../../../components/slides/highlighted/common/TextAreaWithCharacterCounter';

interface IMessagesContainerProps {
    conversation: IConversation | undefined;
    handleDeleteConversation: (conversationId: string) => void;
}

function MessagesContainer({conversation, handleDeleteConversation}: IMessagesContainerProps): JSX.Element {
    const {t} = useTranslation();
    const {conversationId} = useParams();

    const loggedInUser = useSelector(loggedInUserSelector);

    const [message, setMessage] = useState<string>('');
    const [deleteConversationModal, setDeleteConversationModal] = useState<boolean>(false);
    const [selectedConversationIdForDelete, setSelectedConversationIdForDelete] = useState<string>('');

    const containerRef = useRef<HTMLDivElement>(null);

    const {data, hasNextPage, fetchNextPage, refetch} = useInfiniteQuery(
        QUERY_KEYS.conversation.messages(conversationId as string),
        ({pageParam = 1}) =>
            fetchMessages(conversationId as string, {
                pageSize: 50,
                pageNumber: pageParam,
            }),
        {
            staleTime: 0,
            cacheTime: 0,
            getNextPageParam: (data) => {
                return data.pageNumber < data.numberOfElements / data.pageSize ? data.pageNumber + 1 : undefined;
            },
            refetchInterval: 5000,
        }
    );

    const unseenMessagesData = useQuery(QUERY_KEYS.conversation.unseen(conversationId as string), () =>
        fetchUnseenMessages(conversationId as string).then((data) => data || [])
    );

    useEffect(() => {
        if (conversation && conversation.numberOfUnseen && conversation.numberOfUnseen !== 0) {
            unseenMessagesData.refetch();
        }
    }, [conversation, conversation?.numberOfUnseen, unseenMessagesData]);

    const removeUserFromParticipants = (participants: {userId: string; email?: string}[]): string => {
        return participants
            .filter((p) => p.userId !== loggedInUser?._id)
            .map((p) => p.email)
            .join(', ');
    };

    const handleMessageChange = (value: string): void => {
        const truncatedValue = value.length > MAX_MESSAGE_LENGTH ? value.slice(0, MAX_MESSAGE_LENGTH) : value;

        const validation = messageValidator.safeParse(truncatedValue);

        if (validation.success) {
            setMessage(truncatedValue);
        }
    };

    const handleSendMessage: UseMutationResult<void, IUpdateError, {message: string; conversationId: string}> = useMutation(
        (variables: {message: string; conversationId: string}) =>
            sendMessage({text: variables.message, conversationId: variables.conversationId}),
        {
            onSuccess: () => {
                refetch();
                setMessage('');
            },
            onError: () => {
                toast.error(t('toasts.errorOccurred'));
            },
        }
    );

    const deleteConversationAction: UseMutationResult<{success: boolean}, IUpdateError, string> = useMutation(
        (selectedConversationIdForDelete: string) => deleteConversation(selectedConversationIdForDelete),
        {
            onSuccess: () => {
                handleDeleteConversation(selectedConversationIdForDelete);
            },
        }
    );

    const allMessagesData = useMemo(() => {
        return {
            ...data,
            data: {
                pages: [...(unseenMessagesData.data ? [{content: unseenMessagesData.data}] : []), ...(data?.pages || [])],
            },
        };
    }, [data, unseenMessagesData]);

    return (
        <>
            {conversation && (
                <>
                    <div className="p-messaging__content_header">
                        <Button disablePointerEvents leading="solar:user-rounded-linear" variant="ghost" size="lg">
                            {removeUserFromParticipants(conversation.participantsDetailed)}
                        </Button>
                        <div style={{flex: '1'}} />

                        {conversation.connected === false && (
                            <TooltipWrapper id={randomString()} place="left" content={t('messaging.disconnectedPlaceholder')}>
                                <Button size="xl" variant="ghost" color="warning" leading="solar:danger-triangle-bold" />
                            </TooltipWrapper>
                        )}
                        <DropdownMenuCompact
                            menuPosition={DropdownMenuCompactMenuPosition.LEFT}
                            items={[
                                [
                                    {
                                        label: t('common.delete'),
                                        icon: 'solar:trash-bin-trash-linear',
                                        onClick: () => {
                                            setDeleteConversationModal(true);
                                            conversation._id && setSelectedConversationIdForDelete(conversation._id);
                                        },
                                        color: 'error',
                                    },
                                ],
                            ]}
                        >
                            <Button variant="ghost" square leading="ph:dots-three-vertical-bold" />
                        </DropdownMenuCompact>
                    </div>
                    <div
                        id="scrollableMessages"
                        className="messages"
                        ref={containerRef}
                        onScroll={() => {
                            if (containerRef.current?.scrollTop === 0) {
                                fetchNextPage();
                            }
                        }}
                    >
                        {conversation.connected === false && <p className="messages__disconnected">{t('messaging.disconnected')}</p>}{' '}
                        <InfiniteScroll
                            dataLength={allMessagesData.data?.pages.flatMap((page) => page.content).length || 0}
                            next={() => fetchNextPage()}
                            hasMore={hasNextPage || false}
                            inverse
                            loader={hasNextPage && <h4>Loading...</h4>}
                            scrollableTarget="scrollableMessages"
                            style={{display: 'flex', flexDirection: 'column-reverse', gap: '0.5rem'}}
                        >
                            {allMessagesData.data?.pages.flatMap((page) =>
                                page.content.map((message: IMessage) => (
                                    <div
                                        key={message._id}
                                        className={`messages__item messages__item--${message.senderId === loggedInUser?._id ? 'me' : ''}`}
                                    >
                                        <Button
                                            key={message._id}
                                            variant="soft"
                                            color={message.senderId === loggedInUser?._id ? 'info' : 'primary'}
                                            align="right"
                                            disablePointerEvents
                                        >
                                            <div
                                                className={`messages__item_text messages__item_text--${
                                                    message.senderId === loggedInUser?._id ? 'me' : ''
                                                }`}
                                            >
                                                <pre className="messages__item_content">{message.text}</pre>
                                            </div>
                                        </Button>
                                        <div className="messages__item_dateTime">
                                            {message.created && prettyDateFormat(new Date(message.created), i18n.language)}
                                        </div>
                                    </div>
                                ))
                            )}
                        </InfiniteScroll>
                    </div>

                    {conversation.connected && (
                        <div className="p-messaging__form">
                            <div className="p-messaging__form_wrapper">
                                <div className="p-messaging__form_input">
                                    <TextAreaWithCharacterCounter
                                        className="c-sidepanel__input"
                                        value={message}
                                        onChange={handleMessageChange}
                                        placeholder={t('messaging.typeMessage')}
                                        maxLength={MAX_MESSAGE_LENGTH}
                                        onEnterPress={() => {
                                            const isValid = removeHtmlTagsAndSpaces(message) !== null;
                                            if (isValid && conversation?._id) {
                                                handleSendMessage.mutate({
                                                    message,
                                                    conversationId: conversation._id,
                                                });
                                            }
                                        }}
                                        enableSubmitByEnter
                                    />
                                </div>
                                <Button
                                    className="p-messaging__form_button"
                                    data-cy="btnSendMessage"
                                    variant="solid"
                                    size="sm"
                                    color="info"
                                    trailing="stash:paperplane-solid"
                                    disabled={removeHtmlTagsAndSpaces(message) === null}
                                    onClick={(e) => {
                                        e?.preventDefault();
                                        handleSendMessage.mutate({message, conversationId: conversation._id as string});
                                    }}
                                >
                                    {t('messaging.send')}
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
            <ConfirmModal
                message={t('messaging.deleteModal')}
                color="error"
                isOpen={deleteConversationModal}
                onCancel={() => setDeleteConversationModal(false)}
                onConfirm={() => {
                    selectedConversationIdForDelete && deleteConversationAction.mutate(selectedConversationIdForDelete);
                    setDeleteConversationModal(false);
                }}
                showCancel
                showConfirm
                confirmText={t('common.confirm')}
                cancelText={t('common.cancel')}
            />
        </>
    );
}

export default MessagesContainer;
